/**
 * Comprehensive Site Analytics Service
 * Tracks all site interactions, page views, and user behavior
 */

import { databases, ID, Query } from './appwrite';

const DATABASE_ID = import.meta.env.VITE_APPWRITE_DATABASE_ID;
const ANALYTICS_COLLECTION = 'site_analytics';
const SESSIONS_COLLECTION = 'user_sessions';

/**
 * Hash IP address for privacy compliance
 */
const hashIP = async (ip) => {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(ip + 'analytics-salt-2024');
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 64);
  } catch (error) {
    console.error('Error hashing IP:', error);
    return 'unknown';
  }
};

/**
 * Get user's IP address
 */
const getUserIP = async () => {
  console.log('🔍 Getting user IP address...');
  
  try {
    // Try primary IP service (ipify.org)
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      
      const response = await fetch('https://api.ipify.org?format=json', {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🌐 IP from ipify.org:', data.ip);
        return data.ip;
      }
    } catch (error) {
      console.warn('⚠️ Primary IP service failed:', error.message);
    }
    
    // Try backup IP service (httpbin.org)
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      
      const response = await fetch('https://httpbin.org/ip', {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🌐 IP from httpbin.org:', data.origin);
        return data.origin;
      }
    } catch (error) {
      console.warn('⚠️ Backup IP service failed:', error.message);
    }
    
    // Try third backup (jsonip.com)
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      
      const response = await fetch('https://jsonip.com/', {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🌐 IP from jsonip.com:', data.ip);
        return data.ip;
      }
    } catch (error) {
      console.warn('⚠️ Third IP service failed:', error.message);
    }
    
    console.warn('❌ All IP services failed, using fallback');
    return '127.0.0.1';
    
  } catch (error) {
    console.warn('❌ IP lookup failed (using fallback):', error.message);
    return '127.0.0.1';
  }
};

/**
 * Get geolocation from IP - Enhanced with multiple fallback APIs
 */
const getGeolocation = async (ip) => {
  console.log('🌍 Getting geolocation for IP:', ip);
  
  try {
    // Check for local/private IPs
    if (!ip || ip === '127.0.0.1' || ip.startsWith('192.168.') || ip.startsWith('10.') || ip.startsWith('172.16.') || ip.startsWith('172.17.') || ip.startsWith('172.18.') || ip.startsWith('172.19.') || ip.startsWith('172.2') || ip.startsWith('172.30.') || ip.startsWith('172.31.')) {
      console.log('🏠 Local/Private IP detected, using local data');
      return { country: 'Local', countryName: 'Local Network', city: 'Local', region: 'Local' };
    }
    
    // API 1: ipwho.is (most reliable, good rate limits)
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000);
      
      const response = await fetch(`https://ipwho.is/${ip}`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success !== false && data.country_code) {
          console.log('✅ Geolocation from ipwho.is:', data.country);
          return {
            country: data.country_code,
            countryName: data.country,
            city: data.city || 'Unknown',
            region: data.region || 'Unknown'
          };
        }
      }
    } catch (error) {
      console.warn('⚠️ ipwho.is failed:', error.message);
    }
    
    // API 2: ipapi.co
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000);
      
      const response = await fetch(`https://ipapi.co/${ip}/json/`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        if (data.country_code && data.country_code !== 'XX' && !data.error) {
          console.log('✅ Geolocation from ipapi.co:', data.country_name);
          return {
            country: data.country_code,
            countryName: data.country_name,
            city: data.city || 'Unknown',
            region: data.region || 'Unknown'
          };
        }
      }
    } catch (error) {
      console.warn('⚠️ ipapi.co failed:', error.message);
    }
    
    // API 3: freeipapi.com
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000);
      
      const response = await fetch(`https://freeipapi.com/api/json/${ip}`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        if (data.countryCode) {
          console.log('✅ Geolocation from freeipapi.com:', data.countryName);
          return {
            country: data.countryCode,
            countryName: data.countryName,
            city: data.cityName || 'Unknown',
            region: data.regionName || 'Unknown'
          };
        }
      }
    } catch (error) {
      console.warn('⚠️ freeipapi.com failed:', error.message);
    }
    
    // API 4: ip-api.com - REMOVED (HTTP only, causes mixed content warning)
    // Skipping this API to avoid security warnings
    
    // All APIs failed
    console.warn('❌ All geolocation APIs failed for IP:', ip);
    return { country: 'Unknown', countryName: 'Unknown', city: 'Unknown', region: 'Unknown' };
    
  } catch (error) {
    console.warn('❌ Geolocation lookup failed:', error.message);
    return { country: 'Unknown', countryName: 'Unknown', city: 'Unknown', region: 'Unknown' };
  }
};

/**
 * Parse user agent for device/browser info
 */
const parseUserAgent = (userAgent) => {
  const ua = userAgent || navigator.userAgent;
  console.log('🔍 Parsing User Agent:', ua);
  
  // Device type detection
  const isMobile = /Mobile|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const isTablet = /iPad|Android(?!.*Mobile)|Tablet/i.test(ua);
  const deviceType = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop';
  
  // Browser detection (order matters - most specific first)
  let browser = 'Unknown';
  if (ua.includes('Edg/')) {
    browser = 'Edge';
  } else if (ua.includes('OPR/') || ua.includes('Opera/')) {
    browser = 'Opera';
  } else if (ua.includes('Chrome/') && !ua.includes('Edg/')) {
    browser = 'Chrome';
  } else if (ua.includes('Firefox/')) {
    browser = 'Firefox';
  } else if (ua.includes('Safari/') && !ua.includes('Chrome/')) {
    browser = 'Safari';
  } else if (ua.includes('MSIE') || ua.includes('Trident/')) {
    browser = 'Internet Explorer';
  }
  
  // OS detection (more comprehensive)
  let os = 'Unknown';
  if (ua.includes('Windows NT 10.0')) {
    os = 'Windows 10/11';
  } else if (ua.includes('Windows NT 6.3')) {
    os = 'Windows 8.1';
  } else if (ua.includes('Windows NT 6.2')) {
    os = 'Windows 8';
  } else if (ua.includes('Windows NT 6.1')) {
    os = 'Windows 7';
  } else if (ua.includes('Windows')) {
    os = 'Windows';
  } else if (ua.includes('iPhone OS') || ua.includes('iPhone')) {
    const version = ua.match(/iPhone OS (\d+_\d+)/);
    os = version ? `iOS ${version[1].replace('_', '.')}` : 'iOS';
  } else if (ua.includes('iPad')) {
    const version = ua.match(/OS (\d+_\d+)/);
    os = version ? `iPadOS ${version[1].replace('_', '.')}` : 'iPadOS';
  } else if (ua.includes('Mac OS X')) {
    const version = ua.match(/Mac OS X (\d+_\d+)/);
    os = version ? `macOS ${version[1].replace('_', '.')}` : 'macOS';
  } else if (ua.includes('Android')) {
    const version = ua.match(/Android (\d+\.?\d*)/);
    os = version ? `Android ${version[1]}` : 'Android';
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  } else if (ua.includes('CrOS')) {
    os = 'Chrome OS';
  }
  
  const result = { deviceType, browser, os };
  console.log('📊 Parsed User Agent:', result);
  return result;
};

/**
 * Generate or get session ID
 */
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = ID.unique();
    sessionStorage.setItem('analytics_session_id', sessionId);
    sessionStorage.setItem('session_start_time', Date.now().toString());
  }
  return sessionId;
};

/**
 * Record analytics event
 */
export const recordAnalyticsEvent = async (eventData) => {
  try {
    console.log('🔍 Recording analytics event:', eventData);
    
    const ip = await getUserIP();
    const hashedIP = await hashIP(ip);
    const geolocation = await getGeolocation(ip);
    const deviceInfo = parseUserAgent();
    const sessionId = getSessionId();
    
    const analyticsData = {
      // Event details
      contentType: eventData.contentType || 'page',
      contentId: eventData.contentId || null,
      contentSlug: eventData.contentSlug || null,
      eventType: eventData.eventType || 'view',
      pageUrl: window.location.pathname,
      pageTitle: document.title,
      
      // Timing
      timestamp: new Date().toISOString(),
      sessionId,
      
      // User info (privacy-compliant)
      hashedIP,
      country: geolocation.country,
      countryName: geolocation.countryName,
      city: geolocation.city,
      region: geolocation.region,
      
      // Device/Browser
      deviceType: deviceInfo.deviceType,
      browser: deviceInfo.browser,
      os: deviceInfo.os,
      
      // Referrer
      referrer: document.referrer || 'direct',
      referrerDomain: document.referrer ? new URL(document.referrer).hostname : 'direct',
      
      // Additional metadata
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      timeSpent: eventData.timeSpent || 0,
      scrollDepth: eventData.scrollDepth || 0,
      
      // Custom metadata
      metadata: JSON.stringify(eventData.metadata || {})
    };
    
    console.log('📊 Analytics data prepared:', analyticsData);
    
    const result = await databases.createDocument(
      DATABASE_ID,
      ANALYTICS_COLLECTION,
      ID.unique(),
      analyticsData
    );
    
    console.log('✅ Analytics event recorded successfully:', result);
    return { success: true, data: result };
  } catch (error) {
    console.error('❌ Error recording analytics:', error);
    console.error('   Event data:', eventData);
    console.error('   Database ID:', DATABASE_ID);
    console.error('   Collection:', ANALYTICS_COLLECTION);
    return { success: false, error: error.message };
  }
};

/**
 * Track page view
 */
export const trackPageView = async (contentType = 'page', contentId = null, contentSlug = null) => {
  // Create a unique key for this page view
  const pageKey = `${contentType}-${contentId || 'null'}-${contentSlug || 'null'}-${window.location.pathname}`;
  const currentTime = Date.now();
  
  // Check if we've tracked this exact page recently (within 3 seconds)
  const lastTracked = sessionStorage.getItem(`analytics_last_${pageKey}`);
  if (lastTracked && (currentTime - parseInt(lastTracked)) < 3000) {
    console.log('🚫 Preventing duplicate page view tracking:', pageKey);
    return { success: false, error: 'Duplicate tracking prevented' };
  }
  
  // Store the tracking time
  sessionStorage.setItem(`analytics_last_${pageKey}`, currentTime.toString());
  
  return recordAnalyticsEvent({
    contentType,
    contentId,
    contentSlug,
    eventType: 'view'
  });
};

/**
 * Track content interaction
 */
export const trackInteraction = async (contentType, contentId, interactionType, metadata = {}) => {
  return recordAnalyticsEvent({
    contentType,
    contentId,
    eventType: interactionType,
    metadata
  });
};

/**
 * Track time spent on page
 */
export const trackTimeSpent = async (contentType, contentId, timeSpent) => {
  return recordAnalyticsEvent({
    contentType,
    contentId,
    eventType: 'time_spent',
    timeSpent
  });
};

/**
 * Get site overview statistics
 */
export const getSiteOverview = async (days = 30) => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    // Total views
    const totalViews = await databases.listDocuments(
      DATABASE_ID,
      ANALYTICS_COLLECTION,
      [
        Query.equal('eventType', 'view'),
        Query.greaterThanEqual('timestamp', startDate.toISOString()),
        Query.limit(10000)
      ]
    );
    
    // Unique visitors (by hashed IP)
    const uniqueVisitors = new Set(totalViews.documents.map(doc => doc.hashedIP)).size;
    
    // Today's stats
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayViews = await databases.listDocuments(
      DATABASE_ID,
      ANALYTICS_COLLECTION,
      [
        Query.equal('eventType', 'view'),
        Query.greaterThanEqual('timestamp', today.toISOString()),
        Query.limit(10000)
      ]
    );
    
    // This month's stats
    const thisMonth = new Date();
    thisMonth.setDate(1);
    thisMonth.setHours(0, 0, 0, 0);
    const monthViews = await databases.listDocuments(
      DATABASE_ID,
      ANALYTICS_COLLECTION,
      [
        Query.equal('eventType', 'view'),
        Query.greaterThanEqual('timestamp', thisMonth.toISOString()),
        Query.limit(10000)
      ]
    );
    
    // This year's stats
    const thisYear = new Date();
    thisYear.setMonth(0, 1);
    thisYear.setHours(0, 0, 0, 0);
    const yearViews = await databases.listDocuments(
      DATABASE_ID,
      ANALYTICS_COLLECTION,
      [
        Query.equal('eventType', 'view'),
        Query.greaterThanEqual('timestamp', thisYear.toISOString()),
        Query.limit(10000)
      ]
    );
    
    return {
      success: true,
      data: {
        totalViews: totalViews.total,
        uniqueVisitors,
        todayViews: todayViews.total,
        todayUniqueVisitors: new Set(todayViews.documents.map(doc => doc.hashedIP)).size,
        monthViews: monthViews.total,
        monthUniqueVisitors: new Set(monthViews.documents.map(doc => doc.hashedIP)).size,
        yearViews: yearViews.total,
        yearUniqueVisitors: new Set(yearViews.documents.map(doc => doc.hashedIP)).size
      }
    };
  } catch (error) {
    console.error('Error getting site overview:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get content analytics
 */
export const getContentAnalytics = async (contentType, days = 30) => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    const analytics = await databases.listDocuments(
      DATABASE_ID,
      ANALYTICS_COLLECTION,
      [
        Query.equal('contentType', contentType),
        Query.equal('eventType', 'view'),
        Query.greaterThanEqual('timestamp', startDate.toISOString()),
        Query.limit(10000)
      ]
    );
    
    // Group by content ID
    const contentStats = {};
    analytics.documents.forEach(doc => {
      const id = doc.contentId || 'homepage';
      if (!contentStats[id]) {
        contentStats[id] = {
          contentId: id,
          contentSlug: doc.contentSlug,
          title: doc.pageTitle, // enhanced to capture title
          views: 0,
          uniqueVisitors: new Set()
        };
      }
      contentStats[id].views++;
      contentStats[id].uniqueVisitors.add(doc.hashedIP);
      // Update title if it was missing or update to latest
      if (!contentStats[id].title && doc.pageTitle) {
         contentStats[id].title = doc.pageTitle;
      }
    });
    
    // Convert to array and add unique visitor count
    const result = Object.values(contentStats).map(stat => ({
      ...stat,
      uniqueVisitors: stat.uniqueVisitors.size
    }));
    
    return { success: true, data: result };
  } catch (error) {
    console.error('Error getting content analytics:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get geographic analytics
 */
export const getGeographicAnalytics = async (days = 30, contentType = null, contentId = null) => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    const queries = [
      Query.equal('eventType', 'view'),
      Query.greaterThanEqual('timestamp', startDate.toISOString()),
      Query.limit(10000)
    ];
    
    // Add content filtering if specified
    if (contentType) {
      queries.push(Query.equal('contentType', contentType));
    }
    if (contentId) {
      queries.push(Query.equal('contentId', contentId));
    }
    
    const analytics = await databases.listDocuments(
      DATABASE_ID,
      ANALYTICS_COLLECTION,
      queries
    );
    
    // Group by country
    const countryStats = {};
    analytics.documents.forEach(doc => {
      const country = doc.countryName || 'Unknown';
      if (!countryStats[country]) {
        countryStats[country] = {
          country,
          countryCode: doc.country,
          views: 0,
          uniqueVisitors: new Set()
        };
      }
      countryStats[country].views++;
      countryStats[country].uniqueVisitors.add(doc.hashedIP);
    });
    
    // Convert to array and sort by unique visitors (not views)
    const result = Object.values(countryStats)
      .map(stat => ({
        ...stat,
        uniqueVisitors: stat.uniqueVisitors.size
      }))
      .sort((a, b) => b.uniqueVisitors - a.uniqueVisitors);
    
    return { success: true, data: result };
  } catch (error) {
    console.error('Error getting geographic analytics:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get device/browser analytics
 */
export const getDeviceAnalytics = async (days = 30, contentType = null, contentId = null) => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    const queries = [
      Query.equal('eventType', 'view'),
      Query.greaterThanEqual('timestamp', startDate.toISOString()),
      Query.limit(10000)
    ];
    
    // Add content filtering if specified
    if (contentType) {
      queries.push(Query.equal('contentType', contentType));
    }
    if (contentId) {
      queries.push(Query.equal('contentId', contentId));
    }
    
    const analytics = await databases.listDocuments(
      DATABASE_ID,
      ANALYTICS_COLLECTION,
      queries
    );
    
    const deviceStats = {};
    const browserStats = {};
    const osStats = {};
    
    analytics.documents.forEach(doc => {
      // Device stats
      const device = doc.deviceType || 'Unknown';
      deviceStats[device] = (deviceStats[device] || 0) + 1;
      
      // Browser stats
      const browser = doc.browser || 'Unknown';
      browserStats[browser] = (browserStats[browser] || 0) + 1;
      
      // OS stats
      const os = doc.os || 'Unknown';
      osStats[os] = (osStats[os] || 0) + 1;
    });
    
    return {
      success: true,
      data: {
        devices: Object.entries(deviceStats).map(([name, count]) => ({ name, count })),
        browsers: Object.entries(browserStats).map(([name, count]) => ({ name, count })),
        operatingSystems: Object.entries(osStats).map(([name, count]) => ({ name, count }))
      }
    };
  } catch (error) {
    console.error('Error getting device analytics:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get daily trends
 */
export const getDailyTrends = async (days = 30, contentType = null, contentId = null) => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    const queries = [
      Query.equal('eventType', 'view'),
      Query.greaterThanEqual('timestamp', startDate.toISOString()),
      Query.limit(10000)
    ];
    
    // Add content filtering if specified
    if (contentType) {
      queries.push(Query.equal('contentType', contentType));
    }
    if (contentId) {
      queries.push(Query.equal('contentId', contentId));
    }
    
    const analytics = await databases.listDocuments(
      DATABASE_ID,
      ANALYTICS_COLLECTION,
      queries
    );
    
    // Group by date
    const dailyStats = {};
    analytics.documents.forEach(doc => {
      const date = doc.timestamp.split('T')[0]; // Get YYYY-MM-DD
      if (!dailyStats[date]) {
        dailyStats[date] = {
          date,
          views: 0,
          uniqueVisitors: new Set()
        };
      }
      dailyStats[date].views++;
      dailyStats[date].uniqueVisitors.add(doc.hashedIP);
    });
    
    // Convert to array and sort by date
    const result = Object.values(dailyStats)
      .map(stat => ({
        ...stat,
        uniqueVisitors: stat.uniqueVisitors.size
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    
    return { success: true, data: result };
  } catch (error) {
    console.error('Error getting daily trends:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get referrer analytics
 */
export const getReferrerAnalytics = async (days = 30) => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    const analytics = await databases.listDocuments(
      DATABASE_ID,
      ANALYTICS_COLLECTION,
      [
        Query.equal('eventType', 'view'),
        Query.greaterThanEqual('timestamp', startDate.toISOString()),
        Query.limit(10000)
      ]
    );
    
    const referrerStats = {};
    analytics.documents.forEach(doc => {
      const referrer = doc.referrerDomain || 'direct';
      if (!referrerStats[referrer]) {
        referrerStats[referrer] = {
          referrer,
          views: 0,
          uniqueVisitors: new Set()
        };
      }
      referrerStats[referrer].views++;
      referrerStats[referrer].uniqueVisitors.add(doc.hashedIP);
    });
    
    const result = Object.values(referrerStats)
      .map(stat => ({
        ...stat,
        uniqueVisitors: stat.uniqueVisitors.size
      }))
      .sort((a, b) => b.views - a.views);
    
    return { success: true, data: result };
  } catch (error) {
    console.error('Error getting referrer analytics:', error);
    return { success: false, error: error.message };
  }
};